import { Routes } from '@angular/router';
import { keycloakAuthGuard } from './auth/keycloak-auth.guard';
import { inject } from '@angular/core';
import { map, catchError, of } from 'rxjs';
import { InsightsService } from './features/safety/insights.service';

const reviewBreadcrumbResolver = (route: { paramMap: { get: (name: string) => string | null } }) => {
  const insightId = route.paramMap.get('id');

  if (!insightId) {
    return of('Review');
  }

  return inject(InsightsService).getInsightDetail(insightId).pipe(
    map((response) => response.detail?.title || 'Review'),
    catchError(() => of('Review'))
  );
};

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'safety'
  },
  {
    path: 'safety',
    canActivate: [keycloakAuthGuard],
    loadComponent: () => import('./features/safety/safety.component').then(m => m.SafetyComponent),
    data: {
      title: 'Safety'
    }
  },
  {
    path: 'safety/review',
    pathMatch: 'full',
    redirectTo: 'safety'
  },
  {
    path: 'safety/review',
    canActivate: [keycloakAuthGuard],
    data: {
      title: 'Safety'
    },
    children: [
      {
        path: ':id',
        loadComponent: () => import('./features/review/review.component').then(m => m.ReviewComponent),
        resolve: {
          breadcrumb: reviewBreadcrumbResolver
        },
        data: {
          title: 'Review'
        }
      }
    ]
  },
  {
    path: 'library',
    canActivate: [keycloakAuthGuard],
    loadComponent: () => import('./library/library.component').then(m => m.LibraryComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'accordion'
      },
      {
        path: 'accordion',
        loadComponent: () => import('./library/accordion/accordion.component').then(m => m.AccordionComponent),
        data: {
          title: 'Accordion'
        }
      },
      {
        path: 'badges',
        loadComponent: () => import('./library/badges/badges.component').then(m => m.BadgesDemoComponent),
        data: {
          title: 'Badges'
        }
      },
      {
        path: 'banners',
        loadComponent: () => import('./library/banners/banners.component').then(m => m.BannersComponent),
        data: {
          title: 'Banners'
        }
      },
      {
        path: 'briefing',
        loadComponent: () => import('./features/review-components/briefing/briefing-demo.component').then(m => m.BriefingDemoComponent),
        data: {
          title: 'Briefing'
        }
      },
      {
        path: 'buttons',
        loadComponent: () => import('./library/buttons/buttons.component').then(m => m.ButtonsComponent),
        data: {
          title: 'Buttons'
        }
      },
      {
        path: 'cards',
        loadComponent: () => import('./library/cards/cards.component').then(m => m.CardsComponent),
        data: {
          title: 'Cards'
        }
      },
      {
        path: 'charts',
        loadComponent: () => import('./library/charts/charts.component').then(m => m.ChartsComponent),
        data: {
          title: 'Charts'
        }
      },
      {
        path: 'chips',
        loadComponent: () => import('./library/chips/chips.component').then(m => m.ChipsComponent),
        data: {
          title: 'Chips'
        }
      },
      {
        path: 'dialogs',
        loadComponent: () => import('./library/dialog/dialog.component').then(m => m.DialogComponent),
        data: {
          title: 'Dialogs'
        }
      },
      {
        path: 'empty-states',
        loadComponent: () => import('./library/empty-states/empty-states.component').then(m => m.EmptyStatesComponent),
        data: {
          title: 'Empty states'
        }
      },
      {
        path: 'feedback',
        loadComponent: () => import('./library/feedback/feedback-demo.component').then(m => m.FeedbackDemoComponent),
        data: {
          title: 'Feedback'
        }
      },
      {
        path: 'grid',
        loadComponent: () => import('./library/grid/grid.component').then(m => m.GridDemoComponent),
        data: {
          title: 'Grid'
        }
      },
      {
        path: 'icons',
        loadComponent: () => import('./library/icons/icons.component').then(m => m.IconsComponent),
        data: {
          title: 'Icons'
        }
      },
      {
        path: 'indicators',
        loadComponent: () => import('./library/indicators/indicators.component').then(m => m.IndicatorsComponent),
        data: {
          title: 'Indicators'
        }
      },
      {
        path: 'inline-notification',
        loadComponent: () => import('./library/inline-notification/inline-notification.component').then(m => m.InlineNotificationDemoComponent),
        data: {
          title: 'Inline Notification'
        }
      },
      {
        path: 'inputs',
        loadComponent: () => import('./library/inputs/inputs.component').then(m => m.InputsComponent),
        data: {
          title: 'Inputs'
        }
      },
      {
        path: 'kpi-demo',
        loadComponent: () => import('./features/review-components/kpi/kpi-demo.component').then(m => m.KpiDemoComponent),
        data: {
          title: 'KPI Demo'
        }
      },
      {
        path: 'list',
        loadComponent: () => import('./library/list/list.component').then(m => m.ListDemoComponent),
        data: {
          title: 'List'
        }
      },
      {
        path: 'menu',
        loadComponent: () => import('./library/menu/menu.component').then(m => m.MenuComponent),
        data: {
          title: 'Menu'
        }
      },
      {
        path: 'multiselect',
        loadComponent: () => import('./library/multiselect/multiselect.component').then(m => m.MultiselectComponent),
        data: {
          title: 'Multiselect'
        }
      },
      {
        path: 'notification',
        loadComponent: () => import('./library/notification/notification.component').then(m => m.NotificationComponent),
        data: {
          title: 'Notification'
        }
      },
      {
        path: 'panelbar',
        loadComponent: () => import('./library/panelbar/panelbar.component').then(m => m.PanelbarComponent),
        data: {
          title: 'Panelbar'
        }
      },
      {
        path: 'panel-header',
        loadComponent: () => import('./library/panel-header/panel-header.component').then(m => m.PanelHeaderDemoComponent),
        data: {
          title: 'Panel Header'
        }
      },
      {
        path: 'popup',
        loadComponent: () => import('./library/popup/popup.component').then(m => m.PopupComponent),
        data: {
          title: 'Popup'
        }
      },
      {
        path: 'recommendations',
        loadComponent: () => import('./features/review-components/recommendations/recommendations-demo.component').then(m => m.RecommendationsDemoComponent),
        data: {
          title: 'Recommendations'
        }
      },
      {
        path: 'review-charts',
        loadComponent: () => import('./features/review-components/review-charts/review-chart-demo.component').then(m => m.ReviewChartDemoComponent),
        data: {
          title: 'Review Charts'
        }
      },
      {
        path: 'review-table',
        loadComponent: () => import('./features/review-components/review-table/review-table-demo.component').then(m => m.ReviewTableDemoComponent),
        data: {
          title: 'Review Table'
        }
      },
      {
        path: 'safety-card-demo',
        loadComponent: () => import('./library/safety-card/safety-card-demo.component').then(m => m.SafetyCardDemoComponent),
        data: {
          title: 'Safety Card Demo'
        }
      },
      {
        path: 'search-input',
        loadComponent: () => import('./library/search-input/search-input.component').then(m => m.SearchInputDemoComponent),
        data: {
          title: 'Search Input'
        }
      },
      {
        path: 'sidesheet',
        loadComponent: () => import('./library/sidesheet/sidesheet.component').then(m => m.SidesheetComponent),
        data: {
          title: 'Sidesheet'
        }
      },
      {
        path: 'tooltips',
        loadComponent: () => import('./library/tooltip/tooltip.component').then(m => m.TooltipComponent),
        data: {
          title: 'Tooltips'
        }
      },
      {
        path: 'treeview',
        loadComponent: () => import('./library/treeview/treeview.component').then(m => m.TreeviewComponent),
        data: {
          title: 'Treeview'
        }
      },
      {
        path: 'typography',
        loadComponent: () => import('./library/typography/typography.component').then(m => m.TypographyComponent),
        data: {
          title: 'Typography'
        }
      },
      {
        path: '**',
        redirectTo: 'accordion'
      }
    ],
    data: {
      title: 'Library'
    }
  },
  {
    path: 'no-access',
    loadComponent: () => import('./features/no-access/no-access.component').then(m => m.NoAccessComponent),
    data: {
      title: 'No Access'
    }
  },
  {
    path: '**',
    redirectTo: 'safety'
  }
];
